(function () {
  if (window.__cartInsuranceDefaultLoaded) return;
  window.__cartInsuranceDefaultLoaded = true;

  const script = /** @type {HTMLScriptElement|null} */ (
    document.currentScript || document.querySelector('script[src*="cart-insurance"]')
  );
  const INSURANCE_ID = Number(script?.dataset?.insuranceId || 39407882305578);
  const ROOT_SELECTOR = '[data-cart-insurance-opt-in]';
  const CHECKBOX_SELECTOR = '.cart-shipping-protection__checkbox';
  const STATUS_SELECTOR = '.cart-shipping-protection__status';
  const OPT_IN_ATTRIBUTE = '_shipping_protection_opt_in';
  const OPT_OUT_ATTRIBUTE = '_shipping_protection_opt_out';

  /** @typedef {{ variant_id: number, quantity: number }} CartItem */
  /** @typedef {{ items?: CartItem[], sections?: Record<string, string>, attributes?: Record<string, string> }} Cart */

  let autoApplyInFlight = false;
  let cleanupInFlight = false;

  /** @param {Cart|undefined} cart */
  function isCartPayload(cart) {
    return Array.isArray(cart?.items) && !!cart.attributes && typeof cart.attributes === 'object';
  }

  function getSectionIds() {
    return Array.from(
      new Set(
        Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
          .map((element) => element instanceof HTMLElement ? element.dataset.sectionId : '')
          .filter(Boolean)
      )
    );
  }

  /** @param {Cart} cart */
  function merchandiseItemCount(cart) {
    return (cart.items || []).reduce((count, item) => {
      return Number(item.variant_id) === INSURANCE_ID ? count : count + Number(item.quantity || 0);
    }, 0);
  }

  /** @param {Cart} cart */
  function hasInsurance(cart) {
    return (cart.items || []).some((item) => {
      return Number(item.variant_id) === INSURANCE_ID && Number(item.quantity || 0) > 0;
    });
  }

  /** @param {Cart} cart */
  function hasProtectionOptedIn(cart) {
    return cart.attributes?.[OPT_IN_ATTRIBUTE] === 'true';
  }

  /** @param {Cart} cart */
  function hasProtectionOptedOut(cart) {
    return cart.attributes?.[OPT_OUT_ATTRIBUTE] === 'true';
  }

  /** @param {Cart} cart */
  function isProtectionSelected(cart) {
    return hasInsurance(cart) && !hasProtectionOptedOut(cart);
  }

  async function fetchCart() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Could not fetch cart');
    return response.json();
  }

  /**
   * @param {HTMLElement} root
   * @param {string} message
   */
  function setStatus(root, message) {
    const status = root.querySelector(STATUS_SELECTOR);
    if (status) status.textContent = message;
  }

  /**
   * @param {boolean} selected
   * @param {string|undefined} statusMessage
   * @param {boolean|undefined} disabled
   */
  function syncProtectionRoots(selected, statusMessage, disabled) {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (!(root instanceof HTMLElement)) return;
      const checkbox = root.querySelector(CHECKBOX_SELECTOR);
      if (checkbox instanceof HTMLInputElement) {
        checkbox.checked = selected;
        if (disabled !== undefined) checkbox.disabled = disabled;
      }
      if (statusMessage !== undefined) setStatus(root, statusMessage);
    });
  }

  /**
   * @param {string} endpoint
   * @param {Record<string, unknown>} body
   */
  async function mutateCart(endpoint, body) {
    const sectionIds = getSectionIds();
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        ...body,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const payload = await response.json();
    if (!response.ok || payload.errors) {
      throw new Error(typeof payload.errors === 'string' ? payload.errors : 'Cart update failed');
    }

    const cart = isCartPayload(payload) ? payload : await fetchCart();
    cart.sections = payload.sections || cart.sections || {};
    syncProtectionRoots(isProtectionSelected(cart));
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'cart-insurance',
          data: {
            source: 'cart-insurance',
            itemCount: merchandiseItemCount(cart),
            sections: cart.sections,
          },
        },
      })
    );
    return cart;
  }

  async function addInsurance() {
    return mutateCart('/cart/update.js', {
      updates: { [INSURANCE_ID]: 1 },
      attributes: { [OPT_IN_ATTRIBUTE]: 'true', [OPT_OUT_ATTRIBUTE]: '' },
    });
  }

  async function removeInsurance() {
    return mutateCart('/cart/update.js', {
      updates: { [INSURANCE_ID]: 0 },
      attributes: { [OPT_IN_ATTRIBUTE]: '', [OPT_OUT_ATTRIBUTE]: 'true' },
    });
  }

  async function clearProtectionState() {
    return mutateCart('/cart/update.js', {
      updates: { [INSURANCE_ID]: 0 },
      attributes: { [OPT_IN_ATTRIBUTE]: '', [OPT_OUT_ATTRIBUTE]: '' },
    });
  }

  /** @param {Cart|undefined} cartFromEvent */
  async function cleanupEmptyCartProtection(cartFromEvent) {
    if (cleanupInFlight) return cartFromEvent;

    let cart;
    try {
      cart = isCartPayload(cartFromEvent) ? cartFromEvent : await fetchCart();
      const hasProtectionState =
        hasInsurance(cart) || hasProtectionOptedIn(cart) || hasProtectionOptedOut(cart);

      if (merchandiseItemCount(cart) === 0) {
        if (!hasProtectionState) return cart;
        cleanupInFlight = true;
        return await clearProtectionState();
      }
      return cart;
    } catch (error) {
      console.warn('Shipping protection cleanup skipped', error);
      return cartFromEvent;
    } finally {
      cleanupInFlight = false;
    }
  }

  /** @param {Cart|undefined} cartFromEvent */
  async function ensureProtectionAutoApplied(cartFromEvent) {
    if (autoApplyInFlight) return cartFromEvent;

    let cart;
    try {
      cart = isCartPayload(cartFromEvent) ? cartFromEvent : await fetchCart();
      syncProtectionRoots(isProtectionSelected(cart));

      if (merchandiseItemCount(cart) === 0) {
        cart = await cleanupEmptyCartProtection(cart);
        return cart;
      }

      if (hasProtectionOptedOut(cart)) {
        if (hasInsurance(cart)) cart = await removeInsurance();
        return cart;
      }
      if (hasInsurance(cart) && hasProtectionOptedIn(cart)) return cart;
      if (autoApplyInFlight) return cart;

      autoApplyInFlight = true;
      syncProtectionRoots(true, 'Shipping protection selected. Updating total…', true);
      cart = await addInsurance();
      syncProtectionRoots(true, 'Shipping protection added.', false);
      return cart;
    } catch (error) {
      console.warn('Shipping protection auto-apply skipped', error);
      syncProtectionRoots(
        false,
        'Shipping protection could not be added automatically. You can add it here.',
        false
      );
      return cartFromEvent;
    } finally {
      autoApplyInFlight = false;
      syncProtectionRoots(
        cart ? isProtectionSelected(cart) : false,
        undefined,
        false
      );
    }
  }

  /** @param {HTMLElement} root */
  function bindOptIn(root) {
    if (root.dataset.cartInsuranceBound === 'true') return;
    const checkbox = root.querySelector(CHECKBOX_SELECTOR);
    if (!(checkbox instanceof HTMLInputElement)) return;

    root.dataset.cartInsuranceBound = 'true';
    checkbox.addEventListener('change', async () => {
      const targetCheckedState = checkbox.checked;
      syncProtectionRoots(
        targetCheckedState,
        targetCheckedState
          ? 'Shipping protection selected. Updating total…'
          : 'Removing shipping protection…',
        true
      );

      try {
        const cart = await (targetCheckedState ? addInsurance() : removeInsurance());
        const finalCheckedState = isProtectionSelected(cart);
        syncProtectionRoots(
          finalCheckedState,
          finalCheckedState ? 'Shipping protection added.' : 'Shipping protection removed.',
          false
        );
      } catch (error) {
        syncProtectionRoots(
          !targetCheckedState,
          'Shipping protection could not be updated. Please try again.',
          false
        );
        console.error('Shipping protection update failed', error);
      }
    });
  }

  function bindOptIns() {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root instanceof HTMLElement) bindOptIn(root);
    });
  }

  function init() {
    bindOptIns();
    ensureProtectionAutoApplied().catch(() => {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('cart:update', (event) => {
    window.setTimeout(() => {
      bindOptIns();
      ensureProtectionAutoApplied(event?.detail?.resource).catch(() => {});
    }, 0);
  });

  new MutationObserver(bindOptIns).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
